import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getDashboardStats, getRecentActivity, getChartVolume, getChartClassification, getChartConfidence, getAuditStats, getConfidenceTrend, getSuggestions } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import {
  List,
  ClipboardCheck,
  CheckCircle2,
  Filter,
  ArrowRight,
  Brain,
  Building2,
  ShieldCheck,
  Lightbulb,
  Zap,
  AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const CHART_COLORS = ['#0A3622', '#D98A4A', '#2D7A5D', '#4B5563', '#A3A8A4'];
const STATUS_COLORS = {
  'Pending': '#9CA3AF',
  'Classified': '#D98A4A',
  'Needs Review': '#D98A4A',
  'Auto-Posted': '#2D7A5D',
  'Rejected': '#C84B31'
};

function StatCard({ title, value, icon: Icon, color, link, delay }) {
  return (
    <Link
      to={link}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className={`bg-white rounded-xl p-6 card-hover block animate-fade-in-up animate-fade-in-up-delay-${delay}`}
      style={{ border: '1px solid #E5E3DB' }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: '#9CA3AF' }}>{title}</p>
          <p className="text-3xl font-heading font-medium">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: color }}>
          <Icon size={20} className="text-white" strokeWidth={1.8} />
        </div>
      </div>
    </Link>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white rounded-lg p-3 shadow-lg font-body" style={{ border: '1px solid #E5E3DB' }}>
        <p className="text-xs font-mono mb-1" style={{ color: '#9CA3AF' }}>{label}</p>
        {payload.map((p, i) => (
          <p key={p.dataKey || p.name || `payload-${i}`} className="text-sm font-medium">
            {p.name}: <span className="font-mono">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

const scenarioLabels = {
  equipment_above_cap_threshold: 'Equipment/Cap',
  owner_loan_vs_revenue: 'Owner Loan',
  prepayment_vs_earned: 'Prepayment',
  insurance_claim_proceeds: 'Insurance',
  security_deposit: 'Deposit',
  intercompany_transfer: 'Intercompany'
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [volumeData, setVolumeData] = useState([]);
  const [classificationData, setClassificationData] = useState([]);
  const [confidenceData, setConfidenceData] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [suggestionsData, setSuggestionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateParams = {};
      if (dateFrom) dateParams.date_from = dateFrom;
      if (dateTo) dateParams.date_to = dateTo;
      
      const [statsRes, activityRes, volumeRes, classRes, confRes, auditRes, trendRes, suggestRes] = await Promise.all([
        getDashboardStats(),
        getRecentActivity(5),
        getChartVolume(dateParams),
        getChartClassification(dateParams),
        getChartConfidence(dateParams),
        getAuditStats(),
        getConfidenceTrend(),
        getSuggestions()
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setVolumeData(volumeRes.data.data);
      setClassificationData(classRes.data.data);
      setConfidenceData(confRes.data.data);
      setAuditStats(auditRes.data);
      setTrendData(trendRes.data.data);
      setSuggestionsData(suggestRes.data.suggestions || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Pattern vs Context Education Banner */}
        <div className="rounded-xl p-6 animate-fade-in-up" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
          <div className="flex items-start gap-6">
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(224,122,95,0.1)' }}>
                <Brain size={22} style={{ color: '#C84B31' }} strokeWidth={1.8} />
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(198,156,109,0.1)' }}>
                <Building2 size={22} style={{ color: '#D98A4A' }} strokeWidth={1.8} />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-xl font-medium tracking-tight mb-2" style={{ color: '#1A1A1A' }}>
                <span className="italic" style={{ color: '#D98A4A' }}>Pattern Recognition</span> vs Business Context
              </h2>
              <p className="text-sm font-body leading-relaxed" style={{ color: '#4B5563' }}>
                <strong style={{ color: '#C84B31' }}>Pattern Recognition</strong> analyzes vendor names, amounts, and timing. 
                <strong style={{ color: '#D98A4A' }}> Business Context</strong> applies your company's rules and policies that override patterns.
              </p>
            </div>
          </div>
        </div>

        {/* Proactive Suggestions */}
        {suggestionsData.length > 0 && (
          <div className="space-y-3 animate-fade-in-up animate-fade-in-up-delay-1" data-testid="proactive-suggestions">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={16} style={{ color: '#D98A4A' }} strokeWidth={2} />
              <h3 className="text-sm font-body font-semibold" style={{ color: '#1A1A1A' }}>Action Items</h3>
              <span className="px-2.5 py-0.5 text-[10px] rounded-full font-mono" style={{ background: 'rgba(198,156,109,0.15)', color: '#D98A4A' }}>{suggestionsData.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suggestionsData.slice(0, 6).map((s, i) => (
                <Link
                  key={`${s.title}-${i}`}
                  to={s.action?.path || '/dashboard'}
                  data-testid={`suggestion-${i}`}
                  className="bg-white rounded-xl p-5 flex items-start gap-3 transition-all duration-300 hover:shadow-md"
                  style={{ border: `1px solid ${s.priority === 'high' ? '#D98A4A' : '#E5E3DB'}` }}
                >
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{
                    background: s.priority === 'high' ? '#D98A4A' : s.priority === 'medium' ? '#D98A4A' : '#9CA3AF'
                  }}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body font-medium" style={{ color: '#1A1A1A' }}>{s.title}</p>
                    <p className="text-xs font-body mt-1 leading-relaxed" style={{ color: '#4B5563' }}>{s.description}</p>
                    <span className="text-xs font-body font-medium mt-2 inline-block" style={{ color: '#C84B31' }}>{s.action?.label} &rarr;</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard title="Total Transactions" value={stats?.total_transactions || 0} icon={List} color="#0A3622" link="/transactions" delay={1} />
          <StatCard title="Pending Review" value={stats?.pending_review || 0} icon={ClipboardCheck} color="#D98A4A" link="/review" delay={2} />
          <StatCard title="Posted" value={stats?.posted || 0} icon={CheckCircle2} color="#2D7A5D" link="/transactions" delay={3} />
          <StatCard title="Active Rules" value={stats?.active_rules || 0} icon={Filter} color="#4B5563" link="/rules" delay={4} />
        </div>

        {/* Charts Row */}
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-medium" style={{ color: '#1A1A1A' }}>
            <span className="italic" style={{ color: '#D98A4A' }}>Analytics</span>
          </h2>
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); }}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          {/* Transaction Volume */}
          <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }} data-testid="chart-volume">
            <h3 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#9CA3AF' }}>Transaction Volume</h3>
            {volumeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={volumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} stroke="#9CA3AF" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#0A3622" radius={[3, 3, 0, 0]} name="Transactions" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm font-body" style={{ color: '#9CA3AF' }}>No data yet</div>
            )}
          </div>

          {/* Classification Breakdown */}
          <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }} data-testid="chart-classification">
            <h3 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#9CA3AF' }}>Classification Breakdown</h3>
            {classificationData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={classificationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {classificationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2.5">
                  {classificationData.map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between text-sm font-body">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.name] || CHART_COLORS[i] }}></span>
                        <span style={{ color: '#4B5563' }}>{item.name}</span>
                      </div>
                      <span className="font-mono font-medium" style={{ color: '#1A1A1A' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm font-body" style={{ color: '#9CA3AF' }}>No data yet</div>
            )}
          </div>

          {/* Confidence Distribution */}
          <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }} data-testid="chart-confidence">
            <h3 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#9CA3AF' }}>Confidence Distribution</h3>
            {confidenceData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={confidenceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                  <XAxis dataKey="range" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} stroke="#9CA3AF" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Transactions" radius={[4, 4, 0, 0]}>
                    {confidenceData.map((entry, index) => {
                      const colors = ['#C84B31', '#D98A4A', '#D98A4A', '#2D7A5D', '#0A3622'];
                      return <Cell key={`cell-${index}`} fill={colors[index]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm font-body" style={{ color: '#9CA3AF' }}>No analyzed transactions</div>
            )}
          </div>

          {/* Confidence Trend */}
          <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }} data-testid="chart-confidence-trend">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold" style={{ color: '#9CA3AF' }}>Confidence Trend</h3>
              <Link to="/analytics" className="text-xs font-body flex items-center gap-1 transition-colors" style={{ color: '#9CA3AF' }}>
                Detail <ArrowRight size={10} />
              </Link>
            </div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0A3622" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#0A3622" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="week" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono' }} stroke="#9CA3AF" />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="avg_confidence" name="Avg %" stroke="#0A3622" fill="url(#trendGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm font-body" style={{ color: '#9CA3AF' }}>Analyze transactions to track</div>
            )}
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Override Audit Widget */}
          <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }} data-testid="audit-widget">
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB' }}>
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} style={{ color: '#D98A4A' }} strokeWidth={1.8} />
                <h3 className="font-heading text-lg font-medium">Context Overrides</h3>
              </div>
              <Link to="/audit" className="text-xs font-body flex items-center gap-1 transition-colors" style={{ color: '#9CA3AF' }} data-testid="view-all-audits-link">
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <div className="p-6">
              <div className="flex items-end gap-4 mb-5">
                <div className="text-4xl font-heading font-medium" style={{ color: '#1A1A1A' }}>{auditStats?.total_overrides || 0}</div>
                <p className="text-sm font-body mb-1" style={{ color: '#4B5563' }}>total overrides</p>
              </div>
              
              {auditStats?.by_scenario && Object.keys(auditStats.by_scenario).length > 0 && (
                <div className="space-y-2 mb-5">
                  <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold" style={{ color: '#9CA3AF' }}>By Scenario</p>
                  {Object.entries(auditStats.by_scenario).map(([scenario, count]) => (
                    <div key={scenario} className="flex items-center justify-between text-sm font-body">
                      <span className="capitalize" style={{ color: '#4B5563' }}>{scenarioLabels[scenario] || scenario.replace(/_/g, ' ')}</span>
                      <span className="font-mono font-medium" style={{ color: '#1A1A1A' }}>{count}</span>
                    </div>
                  ))}
                </div>
              )}

              {auditStats?.recent?.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid #E5E3DB' }}>
                  <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: '#9CA3AF' }}>Recent Overrides</p>
                  <div className="space-y-3">
                    {auditStats.recent.slice(0, 3).map((entry) => (
                      <div key={entry.audit_id} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#D98A4A' }}></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-body truncate" style={{ color: '#1A1A1A' }}>
                            <span className="font-medium">{entry.vendor_name}</span> - ${Math.abs(entry.amount || 0).toLocaleString()}
                          </p>
                          <p className="text-xs font-body truncate" style={{ color: '#9CA3AF' }}>{entry.review_reason_display}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Learning Stats */}
          <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB' }}>
              <div className="flex items-center gap-3">
                <Lightbulb size={18} style={{ color: '#C84B31' }} strokeWidth={1.8} />
                <h3 className="font-heading text-lg font-medium">Learning Insights</h3>
              </div>
              <Link to="/corrections" className="text-xs font-body flex items-center gap-1 transition-colors" style={{ color: '#9CA3AF' }}>
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <div className="p-6 space-y-4 font-body">
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: '#4B5563' }}>Total Corrections</span>
                <span className="font-mono font-semibold" style={{ color: '#1A1A1A' }}>{stats?.total_corrections || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: '#4B5563' }}>Policy-Level Changes</span>
                <span className="font-mono font-semibold" style={{ color: '#C84B31' }}>{stats?.policy_corrections || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: '#4B5563' }}>Pending Classification</span>
                <span className="font-mono font-semibold" style={{ color: '#1A1A1A' }}>{stats?.pending_classification || 0}</span>
              </div>
              <div className="text-xs pt-4" style={{ borderTop: '1px solid #E5E3DB', color: '#9CA3AF' }}>
                Policy corrections create new context rules automatically
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
          <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB' }}>
            <h3 className="font-heading text-lg font-medium">Recent Transactions</h3>
            <Link to="/transactions" className="text-xs font-body flex items-center gap-1 transition-colors" style={{ color: '#9CA3AF' }}>
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {activity?.recent_transactions?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr className="text-left">
                    <th className="px-6 pt-4">Date</th>
                    <th className="px-6 pt-4">Vendor</th>
                    <th className="px-6 pt-4 text-right">Amount</th>
                    <th className="px-6 pt-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.recent_transactions.map((txn) => (
                    <tr key={txn.transaction_id} className="transition-colors duration-200">
                      <td className="px-6 font-mono text-sm">{txn.date}</td>
                      <td className="px-6 text-sm font-body">{txn.vendor_name}</td>
                      <td className="px-6 text-right font-mono text-sm">
                        ${Math.abs(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6">
                        <span className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full ${
                          txn.status === 'posted' ? 'badge-success' :
                          txn.status === 'pending_review' ? 'badge-warning' :
                          'badge-info'
                        }`}>
                          {txn.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <List size={32} className="mx-auto mb-3" style={{ color: '#9CA3AF' }} />
              <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>No transactions yet</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
